// src/pages/tenant/TenantSSOPage.tsx
// SSO / Identity Provider configuration page for a tenant.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ChipInput } from '@/features/tenant/ChipInput';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SSOProtocol = 'SAML' | 'OIDC';

type SSOStatus = 'DRAFT' | 'TESTING' | 'ACTIVE' | 'ERROR' | 'DISABLED';

type TenantRole = 'OWNER' | 'ADMIN' | 'MANAGER' | 'MEMBER' | 'VIEWER';

const SAML_NAME_ID_FORMATS = [
  'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
] as const;

const SAML_NAME_ID_LABELS: Record<string, string> = {
  'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress': 'Email Address',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent': 'Persistent',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:transient': 'Transient',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified': 'Unspecified',
};

const TENANT_ROLES: TenantRole[] = ['OWNER', 'ADMIN', 'MANAGER', 'MEMBER', 'VIEWER'];

const DEFAULT_OIDC_SCOPES = ['openid', 'profile', 'email'];

interface SSOConfig {
  protocol: SSOProtocol;
  provider_name: string;
  is_enabled: boolean;
  // SAML
  saml_entity_id: string;
  saml_sso_url: string;
  saml_slo_url: string;
  saml_certificate: string;
  saml_name_id_format: string;
  saml_attribute_mapping: string;
  // OIDC
  oidc_issuer_url: string;
  oidc_client_id: string;
  oidc_client_secret: string;
  oidc_scopes: string[];
  oidc_attribute_mapping: string;
  // Behavior
  enforce_sso: boolean;
  auto_provision_users: boolean;
  auto_deprovision_users: boolean;
  default_role: TenantRole;
  allowed_domains: string[];
  // Status (read-only from server)
  status: SSOStatus;
  last_error: string;
  last_successful_login: string;
}

// ---------------------------------------------------------------------------
// Toggle component
// ---------------------------------------------------------------------------

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <div className="min-w-0">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadgeVariant(
  status: SSOStatus,
): 'success' | 'warning' | 'error' | 'default' | 'secondary' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'TESTING':
      return 'default';
    case 'ERROR':
      return 'error';
    case 'DISABLED':
      return 'secondary';
    case 'DRAFT':
    default:
      return 'warning';
  }
}

function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function defaultConfig(): SSOConfig {
  return {
    protocol: 'SAML',
    provider_name: '',
    is_enabled: false,
    saml_entity_id: '',
    saml_sso_url: '',
    saml_slo_url: '',
    saml_certificate: '',
    saml_name_id_format: SAML_NAME_ID_FORMATS[0],
    saml_attribute_mapping: '{}',
    oidc_issuer_url: '',
    oidc_client_id: '',
    oidc_client_secret: '',
    oidc_scopes: [...DEFAULT_OIDC_SCOPES],
    oidc_attribute_mapping: '{}',
    enforce_sso: false,
    auto_provision_users: false,
    auto_deprovision_users: false,
    default_role: 'MEMBER',
    allowed_domains: [],
    status: 'DRAFT',
    last_error: '',
    last_successful_login: '',
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantSSOPage - SSO / Identity Provider configuration.
 * Route: /tenants/:id/settings/sso
 */
export const TenantSSOPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const { t } = useI18n();

  const [config, setConfig] = useState<SSOConfig>(defaultConfig());
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<unknown>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const tenantName = (store.currentItem as any)?.name ?? '';

  const [loadedConfig, setLoadedConfig] = useState<any>(null);

  // Fetch SSO config on mount
  useEffect(() => {
    if (id) {
      store.GetTenantSSOConfig(id).then((res: any) => {
        setLoadedConfig(res?.config ?? null);
      }).catch(() => {});
    }
  }, [id]);

  // Populate local state from loaded config
  useEffect(() => {
    if (loadedConfig) {
      const c = loadedConfig;
      setConfig({
        protocol: c.protocol === 'OIDC' ? 'OIDC' : 'SAML',
        provider_name: c.provider_name ?? '',
        is_enabled: c.is_enabled ?? false,
        saml_entity_id: c.saml_entity_id ?? '',
        saml_sso_url: c.saml_sso_url ?? '',
        saml_slo_url: c.saml_slo_url ?? '',
        saml_certificate: c.saml_certificate ?? '',
        saml_name_id_format: c.saml_name_id_format ?? SAML_NAME_ID_FORMATS[0],
        saml_attribute_mapping:
          typeof c.saml_attribute_mapping === 'object'
            ? JSON.stringify(c.saml_attribute_mapping, null, 2)
            : (c.saml_attribute_mapping ?? '{}'),
        oidc_issuer_url: c.oidc_issuer_url ?? '',
        oidc_client_id: c.oidc_client_id ?? '',
        oidc_client_secret: c.oidc_client_secret ?? '',
        oidc_scopes: Array.isArray(c.oidc_scopes) ? c.oidc_scopes : [...DEFAULT_OIDC_SCOPES],
        oidc_attribute_mapping:
          typeof c.oidc_attribute_mapping === 'object'
            ? JSON.stringify(c.oidc_attribute_mapping, null, 2)
            : (c.oidc_attribute_mapping ?? '{}'),
        enforce_sso: c.enforce_sso ?? false,
        auto_provision_users: c.auto_provision_users ?? false,
        auto_deprovision_users: c.auto_deprovision_users ?? false,
        default_role: c.default_role ?? 'MEMBER',
        allowed_domains: Array.isArray(c.allowed_domains) ? c.allowed_domains : [],
        status: c.status ?? 'DRAFT',
        last_error: c.last_error ?? '',
        last_successful_login: c.last_successful_login ?? '',
      });
    }
  }, [loadedConfig]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleChange = useCallback(
    <K extends keyof SSOConfig>(field: K, value: SSOConfig[K]) => {
      setConfig((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleDiscover = async () => {
    if (!config.oidc_issuer_url) return;
    setDiscovering(true);
    setErrorMessage(null);
    try {
      const wellKnownUrl = config.oidc_issuer_url.replace(/\/+$/, '') + '/.well-known/openid-configuration';
      const response = await fetch(wellKnownUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setConfig((prev) => ({
        ...prev,
        oidc_issuer_url: data.issuer ?? prev.oidc_issuer_url,
      }));
      setSuccessMessage(t('tenant.sso.discoverSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setTestResult(null);
    try {
      const payload: any = { ...config };
      // Parse JSON attribute mappings before sending
      if (config.saml_attribute_mapping) {
        try {
          payload.saml_attribute_mapping = JSON.parse(config.saml_attribute_mapping);
        } catch {
          setErrorMessage(t('tenant.sso.invalidSamlMapping'));
          setSaving(false);
          return;
        }
      }
      if (config.oidc_attribute_mapping) {
        try {
          payload.oidc_attribute_mapping = JSON.parse(config.oidc_attribute_mapping);
        } catch {
          setErrorMessage(t('tenant.sso.invalidOidcMapping'));
          setSaving(false);
          return;
        }
      }
      await store.UpdateTenantSSOConfig(id, { config: payload });
      setSuccessMessage(t('tenant.sso.saveSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!id) return;
    setTesting(true);
    setTestResult(null);
    setErrorMessage(null);
    try {
      const result = await store.TestTenantSSOConfig(id, {});
      const success = (result as any)?.success ?? false;
      const failed = ((result as any)?.results ?? []).filter((r: any) => !r.passed);
      const message =
        failed.map((r: any) => r.message || r.check_name).join('; ') || (success
          ? t('tenant.sso.testSuccess')
          : t('tenant.sso.testFailed'));
      setTestResult({ success, message });
    } catch {
      setTestResult({
        success: false,
        message: t('tenant.sso.testError'),
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setErrorMessage(null);
    try {
      await store.DeleteTenantSSOConfig(id);
      setConfig(defaultConfig());
      setShowDeleteConfirm(false);
      setSuccessMessage(t('tenant.sso.deleteSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setErrorMessage(err);
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (store.isLoading && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error && !store.currentItem) {
    return (
      <div className="space-y-2">
        <ErrorAlert error={store.error} />
        <Link to="/tenants" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {tenantName || t('tenant.details')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-muted-foreground">{t('tenant.settings')}</span>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.sso.title')}</span>
      </nav>

      {/* Page title */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('tenant.sso.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('tenant.sso.description')}
        </p>
      </div>

      {/* Alerts */}
      {successMessage && (
        <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage ? (
        <ErrorAlert error={errorMessage} dismissible onDismiss={() => setErrorMessage(null)} />
      ) : null}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {testResult && (
        <Alert
          variant={testResult.success ? 'success' : 'error'}
          dismissible
          onDismiss={() => setTestResult(null)}
        >
          {testResult.message}
        </Alert>
      )}

      {/* ================================================================= */}
      {/* Status Banner */}
      {/* ================================================================= */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">
                {t('tenant.sso.status.label')}
              </span>
              <Badge variant={statusBadgeVariant(config.status)}>
                {config.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {config.status === 'ERROR' && config.last_error && (
                <span className="text-red-600 dark:text-red-400">
                  {config.last_error}
                </span>
              )}
              {config.status === 'ACTIVE' && config.last_successful_login && (
                <span>
                  {t('tenant.sso.status.lastLogin')}: {formatDateTime(config.last_successful_login)}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Provider */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.sso.provider.title')}</CardTitle>
          <CardDescription>
            {t('tenant.sso.provider.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Protocol selector */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              {t('tenant.sso.provider.protocol')}
            </label>
            <div className="flex rounded-lg border border-border overflow-hidden w-fit">
              <button
                type="button"
                onClick={() => handleChange('protocol', 'SAML')}
                className={`px-5 py-2 text-sm font-medium transition-colors ${
                  config.protocol === 'SAML'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                SAML 2.0
              </button>
              <button
                type="button"
                onClick={() => handleChange('protocol', 'OIDC')}
                className={`px-5 py-2 text-sm font-medium transition-colors border-l border-border ${
                  config.protocol === 'OIDC'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                OIDC
              </button>
            </div>
          </div>

          <Input
            label={t('tenant.sso.provider.providerName')}
            type="text"
            value={config.provider_name}
            onChange={(e) => handleChange('provider_name', e.target.value)}
            placeholder="e.g. Okta, Azure AD, Google Workspace"
          />

          <Toggle
            checked={config.is_enabled}
            onChange={(value) => handleChange('is_enabled', value)}
            label={t('tenant.sso.provider.enabled')}
            description={t('tenant.sso.provider.enabledDescription')}
          />
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* SAML Configuration */}
      {/* ================================================================= */}
      {config.protocol === 'SAML' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tenant.sso.saml.title')}</CardTitle>
            <CardDescription>
              {t('tenant.sso.saml.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label={t('tenant.sso.saml.entityId')}
              type="text"
              value={config.saml_entity_id}
              onChange={(e) => handleChange('saml_entity_id', e.target.value)}
              placeholder="https://idp.example.com/metadata"
              required
            />
            <Input
              label={t('tenant.sso.saml.ssoUrl')}
              type="url"
              value={config.saml_sso_url}
              onChange={(e) => handleChange('saml_sso_url', e.target.value)}
              placeholder="https://idp.example.com/sso/saml"
              required
            />
            <Input
              label={t('tenant.sso.saml.sloUrl')}
              type="url"
              value={config.saml_slo_url}
              onChange={(e) => handleChange('saml_slo_url', e.target.value)}
              placeholder="https://idp.example.com/slo/saml"
            />
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.saml.certificate')}
              </label>
              <textarea
                value={config.saml_certificate}
                onChange={(e) => handleChange('saml_certificate', e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                rows={6}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('tenant.sso.saml.certificateHint')}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.saml.nameIdFormat')}
              </label>
              <Select
                value={config.saml_name_id_format}
                onChange={(e) => handleChange('saml_name_id_format', e.target.value)}>
                {SAML_NAME_ID_FORMATS.map((fmt) => (
                  <option key={fmt} value={fmt}>
                    {SAML_NAME_ID_LABELS[fmt] ?? fmt}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.saml.attributeMapping')}
              </label>
              <textarea
                value={config.saml_attribute_mapping}
                onChange={(e) => handleChange('saml_attribute_mapping', e.target.value)}
                placeholder={'{\n  "email": "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",\n  "name": "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"\n}'}
                rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('tenant.sso.saml.attributeMappingHint')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================================================================= */}
      {/* OIDC Configuration */}
      {/* ================================================================= */}
      {config.protocol === 'OIDC' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tenant.sso.oidc.title')}</CardTitle>
            <CardDescription>
              {t('tenant.sso.oidc.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Input
                    label={t('tenant.sso.oidc.issuerUrl')}
                    type="url"
                    value={config.oidc_issuer_url}
                    onChange={(e) => handleChange('oidc_issuer_url', e.target.value)}
                    placeholder="https://accounts.google.com"
                    required
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleDiscover}
                  disabled={discovering || !config.oidc_issuer_url.trim()}
                  className="flex-shrink-0"
                >
                  {discovering ? t('common.loading') : t('tenant.sso.oidc.discover')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('tenant.sso.oidc.issuerUrlHint')}
              </p>
            </div>
            <Input
              label={t('tenant.sso.oidc.clientId')}
              type="text"
              value={config.oidc_client_id}
              onChange={(e) => handleChange('oidc_client_id', e.target.value)}
              placeholder="your-client-id"
              required
            />
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.oidc.clientSecret')}
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={config.oidc_client_secret}
                  onChange={(e) => handleChange('oidc_client_secret', e.target.value)}
                  placeholder="your-client-secret"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showSecret ? t('tenant.sso.oidc.hideSecret') : t('tenant.sso.oidc.showSecret')}
                >
                  {showSecret ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Scopes */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.oidc.scopes')}
              </label>
              <ChipInput
                value={config.oidc_scopes}
                onChange={(scopes) => setConfig((prev) => ({ ...prev, oidc_scopes: scopes }))}
                placeholder="e.g. groups"
                aria-label={t('tenant.sso.oidc.scopes')}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {t('tenant.sso.oidc.attributeMapping')}
              </label>
              <textarea
                value={config.oidc_attribute_mapping}
                onChange={(e) => handleChange('oidc_attribute_mapping', e.target.value)}
                placeholder={'{\n  "email": "email",\n  "name": "name",\n  "groups": "groups"\n}'}
                rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('tenant.sso.oidc.attributeMappingHint')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Behavior */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenant.sso.behavior.title')}</CardTitle>
          <CardDescription>
            {t('tenant.sso.behavior.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Toggle
            checked={config.enforce_sso}
            onChange={(value) => handleChange('enforce_sso', value)}
            label={t('tenant.sso.behavior.enforceSso')}
            description={t('tenant.sso.behavior.enforceSsoDescription')}
          />
          <div className="border-t border-border pt-5">
            <Toggle
              checked={config.auto_provision_users}
              onChange={(value) => handleChange('auto_provision_users', value)}
              label={t('tenant.sso.behavior.autoProvision')}
              description={t('tenant.sso.behavior.autoProvisionDescription')}
            />
          </div>
          <div className="border-t border-border pt-5">
            <Toggle
              checked={config.auto_deprovision_users}
              onChange={(value) => handleChange('auto_deprovision_users', value)}
              label={t('tenant.sso.behavior.autoDeprovision')}
              description={t('tenant.sso.behavior.autoDeprovisionDescription')}
            />
          </div>
          <div className="border-t border-border pt-5">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('tenant.sso.behavior.defaultRole')}
            </label>
            <Select
              value={config.default_role}
              onChange={(e) => handleChange('default_role', e.target.value as TenantRole)} className="max-w-xs">
              {TENANT_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.charAt(0) + role.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
          </div>
          <div className="border-t border-border pt-5">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('tenant.sso.behavior.allowedDomains')}
            </label>
            <ChipInput
              value={config.allowed_domains}
              onChange={(domains) => setConfig((prev) => ({ ...prev, allowed_domains: domains }))}
              placeholder="example.com"
              aria-label={t('tenant.sso.behavior.allowedDomains')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.sso.behavior.allowedDomainsHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Action Buttons */}
      {/* ================================================================= */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Button onClick={handleSave} disabled={saving} className="min-w-[160px]">
          {saving ? t('common.saving') : t('tenant.sso.actions.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={handleTest}
          disabled={testing}
          className="min-w-[160px]"
        >
          {testing ? t('common.loading') : t('tenant.sso.actions.test')}
        </Button>
        <div className="sm:ml-auto">
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
            className="min-w-[160px] w-full sm:w-auto"
          >
            {t('tenant.sso.actions.delete')}
          </Button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-destructive">
                {t('tenant.sso.deleteConfirm.title')}
              </CardTitle>
              <CardDescription>
                {t('tenant.sso.deleteConfirm.description')}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? t('common.loading') : t('tenant.sso.deleteConfirm.confirm')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
});

export default TenantSSOPage;
